import { Grid } from '@mui/material'
import { EventDto } from '../../../interfaces/IEvent'
import { EventStatus, EventType } from '../../../interfaces/IEventEnums'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import InputField from '../../../components/InputFields/InputField'
import AutocompleteField from '../../../components/InputFields/AutocompleteField'
import DateTimePickerField from '../../../components/InputFields/DateTimePickerField'
import { FieldName } from '../../../interfaces/IFields'
import MonoInputField from '../../../components/InputFields/MonoInputField'

interface Props {
  onUpdate: (updatedEvent: EventDto) => void
  preDefinedFromDate?: Date
}

function CreateGenericEventGrid({ onUpdate, preDefinedFromDate }: Props) {
  const [genericEvent, setGenericEvent] = useState({
    type: EventType.Generell,
    eventDetails: {
      title: '',
      status: EventStatus.Apen,
      fromDate: preDefinedFromDate ?? new Date(),
      toDate: null,
      description: '',
      responsible: {},
      externalReference: '',
    },
  } as EventDto)

  const handleSelectChange = useCallback((name: string, value: EventStatus | Date | null) => {
    setGenericEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        [name]: value,
      },
    }))
  }, [])

  const handleStartDateChange = useCallback((date: Date | null) => {
    if (!date) return
    setGenericEvent((prevState) => {
      const eventToDate = prevState.eventDetails.toDate ? new Date(prevState.eventDetails.toDate) : null
      const toDate = eventToDate && date > eventToDate ? date : prevState.eventDetails.toDate
      return {
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          fromDate: date,
          toDate,
        },
      }
    })
  }, [])

  const handleEndDateChange = useCallback((date: Date | null) => {
    setGenericEvent((prevState) => {
      const eventFromDate = new Date(prevState.eventDetails.fromDate)
      if (date && eventFromDate > date) {
        return {
          ...prevState,
          eventDetails: {
            ...prevState.eventDetails,
            toDate: eventFromDate,
          },
        }
      }
      return {
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          toDate: date,
        },
      }
    })
  }, [])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    if (name === FieldName.Name || name === FieldName.Id) {
      setGenericEvent((prevState) => ({
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          responsible: {
            ...prevState.eventDetails.responsible,
            [name]: value,
          },
        },
      }))
      return
    }

    setGenericEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        [name]: value,
        responsible: {
          ...prevState.eventDetails.responsible,
        },
      },
    }))
  }, [])

  useEffect(() => {
    onUpdate(genericEvent)
  }, [genericEvent])

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 7,
        }}
      >
        <InputField
          name={FieldName.Title}
          title="Navn på hendelse"
          size="medium"
          value={genericEvent.eventDetails.title}
          required={true}
          onChange={handleInputChange}
          maxLength={80}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 5,
        }}
      >
        <InputField
          name={FieldName.Name}
          title="Ansvarlig"
          size="medium"
          placeholder="Ansvarlig for retting"
          value={genericEvent.eventDetails.responsible.name}
          onChange={handleInputChange}
          multiline={true}
          maxLength={30}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <AutocompleteField<EventStatus>
          name={FieldName.Status}
          title="Status"
          placeholder="Ingen valgt"
          options={Object.values(EventStatus)}
          onChange={(value: EventStatus | null) => handleSelectChange(FieldName.Status, value)}
          getOptionLabel={(status) => status}
          isOptionEqualToValue={(option, value) => option === value}
          required={true}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <DateTimePickerField
          name={FieldName.FromDate}
          title="Startdato"
          required={true}
          value={genericEvent.eventDetails.fromDate}
          onChange={handleStartDateChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <DateTimePickerField
          name={FieldName.ToDate}
          title="Sluttdato"
          disablePast={true}
          minDateTime={genericEvent.eventDetails.fromDate}
          value={genericEvent.eventDetails.toDate}
          onChange={handleEndDateChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 12,
        }}
      >
        <InputField
          name={FieldName.ExternalReference}
          title="Ekstern referanse"
          size="medium"
          value={genericEvent.eventDetails.externalReference}
          onChange={handleInputChange}
          multiline={true}
          maxLength={25}
        />
      </Grid>
      <Grid size={12}>
        <MonoInputField
          name={FieldName.Description}
          title="Beskrivelse"
          size="medium"
          value={genericEvent.eventDetails.description}
          onChange={handleInputChange}
          multiline={true}
          rows={6}
          maxLength={1000}
        />
      </Grid>
    </>
  )
}

export default CreateGenericEventGrid
